import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetVehicleWarrantyClaimsMutation } from '../../../../api/endpoints/vehicleWarrantyClaimsReport';
import { Roles } from '../../../../utils/roleEnums';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useLanguage } from '../../../../contexts/LanguageContext';

export default function useVehicleWarrantyClaimsReport() {
    const { hasAnyRole } = usePermissions();
    const { getCurrentLanguageId } = useLanguage();
    const isDealer = hasAnyRole([Roles.DEALER, Roles.SERVICE_DEALER, Roles.SALES_DEALER]);
    const initialFilters = {
        dealer: [],
        dealerCode: [],
        sasiNo: [],
        country: [],
        warrClaimNo: undefined,
        workOrderNo: undefined,
        modelName: undefined,
        warrTypeList: [],
        workOrderNoStart: undefined,
        workOrderNoEnd: undefined,
        sentToApprovalStartDate: undefined,
        sentToApprovalEndDate: undefined, 
        claimStatus: undefined,
        code: undefined,
        licensePlate: undefined,
    };
    const initialLazyParams = { first: 0, rows: 10, page: 1 };
    const [lazyParams, setLazyParams] = useState(initialLazyParams);
    const [hasApplied, setHasApplied] = useState(isDealer);

    const [activeFilters, setActiveFilters] = useState(initialFilters);

    const [getVehicleWarrantyClaims, { data, isLoading: isFetching, isError, error }] = 
        useGetVehicleWarrantyClaimsMutation();

    const queryBody = useMemo(() => {
        const finalFilters={
             pageNumber: lazyParams.page,
         pageSize: lazyParams.rows,
         dealer: activeFilters.dealer?.length > 0 ? activeFilters.dealer : undefined,
         dealerCode: activeFilters.dealerCode?.length > 0 ? activeFilters.dealerCode : undefined, 
         sasiNo: activeFilters.sasiNo?.length > 0 ? activeFilters.sasiNo : undefined,
         country: activeFilters.country?.length > 0 ? activeFilters.country : undefined,
         warrClaimNo: activeFilters.warrClaimNo || undefined,
         workOrderNo: activeFilters.workOrderNo != null && activeFilters.workOrderNo !== ''
            ? Number(activeFilters.workOrderNo)
            : undefined,
         modelName: activeFilters.modelName || undefined,
         warrTypeList: activeFilters.warrTypeList?.length > 0 ? activeFilters.warrTypeList : undefined,
         workOrderNoStart: activeFilters.workOrderNoStart ?? undefined,
         workOrderNoEnd: activeFilters.workOrderNoEnd ?? undefined,
         sentToApprovalStartDate: activeFilters.sentToApprovalStartDate ?? undefined,
         sentToApprovalEndDate: activeFilters.sentToApprovalEndDate ?? undefined,
        claimStatus: activeFilters.claimStatus ?? undefined,
        code: activeFilters.code || undefined,
        licensePlate: activeFilters.licensePlate || undefined,
        languageId: getCurrentLanguageId(),

        };
        return {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            ...finalFilters,
        };
       
        
    }, [lazyParams.page, lazyParams.rows, activeFilters]);

     const exportParams = useMemo(() => ({
        dealer: activeFilters.dealer?.length > 0 ? activeFilters.dealer : null,
       dealerCode: activeFilters.dealerCode?.length > 0 ? activeFilters.dealerCode : null, 
        sasiNo: activeFilters.sasiNo?.length > 0 ? activeFilters.sasiNo : null,
        country: activeFilters.country?.length > 0 ? activeFilters.country : null,
        warrClaimNo: activeFilters.warrClaimNo || null,
        workOrderNo: activeFilters.workOrderNo != null && activeFilters.workOrderNo !== ''
            ? Number(activeFilters.workOrderNo)
            : null,
        modelName: activeFilters.modelName || null,
        warrTypeList: activeFilters.warrTypeList?.length > 0 ? activeFilters.warrTypeList : null,
        workOrderNoStart: activeFilters.workOrderNoStart ?? null,
        workOrderNoEnd: activeFilters.workOrderNoEnd ?? null,
        sentToApprovalStartDate: activeFilters.sentToApprovalStartDate ?? null,
        sentToApprovalEndDate: activeFilters.sentToApprovalEndDate ?? null,
        claimStatus: activeFilters.claimStatus ?? null,
        code: activeFilters.code || null,
        licensePlate: activeFilters.licensePlate || null,
        languageId: getCurrentLanguageId(),
    }), [activeFilters, getCurrentLanguageId]);

    useEffect(() => {
        if (hasApplied) {
            getVehicleWarrantyClaims(queryBody);
        }
    }, [hasApplied, queryBody, getVehicleWarrantyClaims]);

    const items = hasApplied && data ? (Array.isArray(data.items) ? data.items : []) : [];
    const totalCount = hasApplied && data ? (data.totalCount ?? 0) : 0;

    const applyFilters = useCallback((filters) => {
        setActiveFilters({
            dealer: filters.dealer ? [...filters.dealer] : [],
            dealerCode: filters.dealerCode ? [...filters.dealerCode] : [],
            sasiNo: filters.sasiNo ? [...filters.sasiNo] : [],
            country: filters.country ? [...filters.country] : [],
            warrClaimNo: filters.warrClaimNo ?? undefined,
            workOrderNo: filters.workOrderNo ?? undefined,
            modelName: filters.modelName ?? undefined,
            warrTypeList: filters.warrTypeList ? [...filters.warrTypeList] : [],
            workOrderNoStart: filters.workOrderNoStart ?? undefined,
            workOrderNoEnd: filters.workOrderNoEnd ?? undefined,
            sentToApprovalStartDate: filters.sentToApprovalStartDate ?? undefined,
            sentToApprovalEndDate: filters.sentToApprovalEndDate ?? undefined,
            claimStatus: filters.claimStatus ?? undefined,
            code: filters.code ?? undefined,
            licensePlate: filters.licensePlate ?? undefined,
        });
        setLazyParams(initialLazyParams);
        setHasApplied(true);
    }, [initialLazyParams]);

    const resetFilters = useCallback(() => {
        setActiveFilters(initialFilters);
        setLazyParams(initialLazyParams);
        setHasApplied(isDealer);
    }, [initialFilters, initialLazyParams]);

    const onPage = useCallback((e) => {
        setLazyParams({ first: e.first, rows: e.rows, page: e.page + 1 });
    }, []);

    return {
        items,
        totalCount,
        isFetching,
        isError,
        error,
        lazyParams,
        hasApplied,
        isDealer,
        applyFilters,
        resetFilters,
        onPage,
        exportParams,
    };
}